import { useContext, useState } from "react";
import { AlertTriangle, ChevronDown, Eye, Plus, Pencil, Trash2 } from "lucide-react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import AccountCenterContext from "../../../utils/CostCenterAccHelper";
import AccountContext from "../../../utils/AccountsHelper";
import NoDataFound from "../NoDataFound";
import { useTenant } from "../../../hooks";
import axios from "axios";
import MwSpinnerButton from "../MwSpinnerButton";
import ConfirmDelete from "../ConfirmDelete";
import MwToast from "../MwToast";
import LevelContext from "../../../utils/UserLevel";

const TreeNode = ({
  node,
  level = 0,
  setIsOpen,
  setActionData,
  dispatch,
  isRtl,
  setCode,
  setLoadingchild,
  openNodes,
  setOpenNodes,
  deleteAccount,
  isAccountCenter,
  t,
  onDeleteBlocked,
}) => {
  const { chart_account_level_limit , cost_center_level_limit } = useContext(LevelContext);
  const MAX_SUB_ACCOUNT_LEVEL = chart_account_level_limit;
  const MAX_SUB_COST_CENTER_ACCOUNT_LEVEL = cost_center_level_limit;

  const [show, setShow] = useState(false);
  const hasChildren = node.childs && node.childs.length > 0;
  const isForceOpenNode = false;
  const isOpenNode =
    hasChildren &&
    (level === 0 ? openNodes[node.id] !== false : openNodes[node.id] === true);
  const parsedNodeLevel = Number(node?.level);
  const nodeLevel =
    Number.isFinite(parsedNodeLevel) && parsedNodeLevel > 0
      ? parsedNodeLevel
      : level + 1;
  
  const maxLevel = isAccountCenter ? MAX_SUB_COST_CENTER_ACCOUNT_LEVEL : MAX_SUB_ACCOUNT_LEVEL;
  const canAddSubAccount = nodeLevel < maxLevel;
  
  const addDisabledTitle = isRtl
    ? `لا يمكن إضافة ${isAccountCenter ? "مركز تكلفة فرعي" : "حساب فرعي"} بعد المستوى المحدد ${maxLevel}`
    : `You can only add sub-${isAccountCenter ? "cost center" : "account"} up to level ${maxLevel}`;

  const BaseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const { tenantId, companySelected, branchSelected } = useTenant();

  const GetCode = async (id) => {
    if (!tenantId || !companySelected?.value || !branchSelected?.value) {
      console.warn(
        "Missing tenant/company/branch while generating account code",
        {
          tenantId,
          companyId: companySelected?.value,
          branchId: branchSelected?.value,
        },
      );
      return null;
    }

    const accountPath = isAccountCenter ? "tree-cost-center" : "tree-account";

    try {
      setLoadingchild(true);

      const res = await axios.post(
        `${BaseUrl}/${accountPath}/genAccountNo/${id}`,
        {
          tenant_id: tenantId,
          company_id: companySelected?.value,
          branch_id: branchSelected?.value,
        },
      );

      return res?.data?.data;
    } catch (err) {
      console.error(
        "Failed to generate account code",
        err?.response?.data || err?.message || err,
      );
      return null;
    } finally {
      setLoadingchild(false);
    }
  };

  const accountName = isRtl
    ? node.name_ar || node.accountNameEN
    : node.name_en || node.accountNameAR;
  const deleteBlockedMsg = t("accounts.deleteChildrenFirst");

  const handleAdd = async (e) => {
    e.stopPropagation();
    if (!canAddSubAccount) return;

    setActionData({
      mode: "add",
      parentId: node.id,
      accountType: "secondary",
      account: null,
    });

    setIsOpen(true);

    setOpenNodes((prev) => ({
      ...prev,
      [node.id]: true,
    }));

    setCode(null);
    const NewCode = await GetCode(node?.id);
    setCode(NewCode);
  };

  const handleEdit = (e) => {
    e.stopPropagation();

    setActionData({
      mode: "edit",
      parentId: node.id || null,
      account: node,
    });

    setIsOpen(true);
  };

  const handleView = (e) => {
    e.stopPropagation();

    setActionData({
      mode: "view",
      parentId: node.id || null,
      account: node,
    });

    setIsOpen(true);
  };

  const handleDelete = async (e) => {
    if (hasChildren) {
      if (onDeleteBlocked) onDeleteBlocked(deleteBlockedMsg);
      setShow(false);
      return;
    }
    await deleteAccount(node.id);

    setOpenNodes((prev) => ({
      ...prev,
      [node.parent_id]: true,
    }));
  };
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      if (onDeleteBlocked) onDeleteBlocked(deleteBlockedMsg);
      return;
    }
    setShow(true);
  };

  return (
    <div className="relative">
      <div
        style={{
          paddingInlineStart: level > 0 ? `${level * 24 + 8}px` : "0px",
        }}
      >
        <div className="group flex items-center justify-between py-1.5 px-2 rounded-lg transition-colors duration-300 hover:bg-blue-50 relative">
          {level > 0 && (
            <div
              className="absolute z-10 top-0 bottom-0 pointer-events-none"
              style={{
                insetInlineStart: `${-level * 24 + 12}px`,
                borderInlineStart: "2px solid rgba(38, 23, 89, 0.35)",
              }}
            />
          )}

          <div
            className="flex items-center gap-1.5 cursor-pointer flex-1"
            onClick={() =>
              hasChildren &&
              !isForceOpenNode &&
              setOpenNodes((prev) => ({
                ...prev,
                [node.id]: !prev[node.id],
              }))
            }
          >
            <div className="w-4 h-4 flex items-center justify-center text-[#261759] transition-transform duration-300 flex-shrink-0">
              {hasChildren ? (
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${
                    isOpenNode ? "rotate-0" : isRtl ? "rotate-90" : "-rotate-90"
                  }`}
                />
              ) : (
                <span className="w-2 h-2 border border-[#261759] rounded-full" />
              )}
            </div>

            <span
              className={`text-md ${
                hasChildren
                  ? "font-bold text-[#261759]/90"
                  : "text-[#261759]/90 font-medium"
              }`}
            >
              <span dir="ltr" className="inline-block">
                {node.account_no}
              </span>
              {" - "}
              {accountName}
            </span>
          </div>

          <div
            className="flex items-center bg-blue-50 p-1 rounded gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ marginInlineStart: "0.5rem" }}
          >
            <button
              onClick={handleAdd}
              title={canAddSubAccount ? t("common.add") : addDisabledTitle}
              disabled={!canAddSubAccount}
              className={`p-1.5 rounded-md transition-colors ${
                canAddSubAccount
                  ? "hover:bg-green-100"
                  : "opacity-50 cursor-not-allowed"
              }`}
            >
              <Plus size={14} className="text-green-600" />
            </button>

            <button
              onClick={handleView}
              title={t("dropdown.preview")}
              className="p-1.5 hover:bg-sky-100 rounded-md transition-colors"
            >
              <Eye size={14} className="text-sky-600" />
            </button>

            <button
              onClick={handleEdit}
              title={t("common.edit")}
              className="p-1.5 hover:bg-yellow-100 rounded-md transition-colors"
            >
              <Pencil size={14} className="text-yellow-600" />
            </button>

            <button
              onClick={handleDeleteClick}
              title={hasChildren ? deleteBlockedMsg : t("common.delete")}
              className="p-1.5 hover:bg-red-100 rounded-md transition-colors"
            >
              <Trash2 size={14} className="text-red-600" />
            </button>
          </div>
        </div>
      </div>

      <ConfirmDelete
        onShow={show}
        msg={t("common.confirmDel")}
        onDeleteHandler={() => handleDelete()}
        onCancelHandle={() => setShow(false)}
        setOnShow={() => setShow(false)}
        type={"delete"}
      />

      {hasChildren && isOpenNode && (
        <div className="relative">
          {node.childs.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              setIsOpen={setIsOpen}
              setActionData={setActionData}
              dispatch={dispatch}
              isRtl={isRtl}
              setCode={setCode}
              setLoadingchild={setLoadingchild}
              openNodes={openNodes}
              setOpenNodes={setOpenNodes}
              deleteAccount={deleteAccount}
              isAccountCenter={isAccountCenter}
              t={t}
              onDeleteBlocked={onDeleteBlocked}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function AccountsTree({
  setIsOpen,
  setActionData,
  code,
  setCode,
  loadingChild,
  setLoadingchild,
  editAccount,
  deleteAccount,
}) {
  const { pathname } = useLocation();
  const [openNodes, setOpenNodes] = useState({});
  const [isShowToast, setIsShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const isAccountCenter = pathname.toLowerCase().includes("accountcenter");

  const activeContext = isAccountCenter ? AccountCenterContext : AccountContext;

  const { accounts, dispatch, loading } = useContext(activeContext);
  const { chart_account_level_limit, cost_center_level_limit } =
    useContext(LevelContext) || {};

  const [t] = useTranslation("global");
  const isRtl = i18next.dir(i18next.language) === "rtl";
  const pageMaxLevel = isAccountCenter
    ? cost_center_level_limit
    : chart_account_level_limit;
  const pageLevelNotice =
    pageMaxLevel === null || pageMaxLevel === undefined
      ? ""
      : isRtl
        ? isAccountCenter
          ? `تنبيه : الحد الاقصي لمستوي مراكز التكلفة هو المستوي ${pageMaxLevel}`
          : `${t("accounts.maxAccountsLevelNotice")}${pageMaxLevel}`
        : `The allowed ${isAccountCenter ? "cost center" : "account"} level on this page is ${pageMaxLevel}`;
  const handleDeleteBlocked = (msg) => {
    setToastMsg(msg);
    setIsShowToast(true);
  };

  return (
    <div
      className="relative bg-white rounded-lg shadow-md p-6 w-full"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <MwToast msg={toastMsg} isShow={isShowToast} setIsShow={setIsShowToast} />
      {pageLevelNotice && (
        <div className=" flex items-center gap-2 rounded-lg  border-yellow-700 px-4 w-max mb-1 py-2 border text-sm font-[700] text-yellow-800">
          <AlertTriangle size={18} className="shrink-0 text-yellow-800" />
          {pageLevelNotice}
        </div>
      )}
      <div
        className="space-y-1"
        style={{ textAlign: isRtl ? "right" : "left" }}
      >
        {loading ? (
          <div className="w-full h-[20vh] flex justify-center items-center">
            <MwSpinnerButton />
          </div>
        ) : accounts && accounts.length > 0 ? (
          accounts.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              setIsOpen={setIsOpen}
              setActionData={setActionData}
              openNodes={openNodes}
              setOpenNodes={setOpenNodes}
              dispatch={dispatch}
              isRtl={isRtl}
              t={t}
              isAccountCenter={isAccountCenter}
              setCode={setCode}
              loadingChild={loadingChild}
              setLoadingchild={setLoadingchild}
              editAccount={editAccount}
              deleteAccount={deleteAccount}
              onDeleteBlocked={handleDeleteBlocked}
            />
          ))
        ) : (
          <div className="w-full flex justify-center items-center">
            <NoDataFound msg={t("accounts.noAccountsFound")} />
          </div>
        )}
      </div>
    </div>
  );
}
